import React, { useContext, useState } from 'react';
import { CRMContext } from '../context/CRMContext';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, addDays, subDays, isSameDay, isToday } from 'date-fns';
import { es } from 'date-fns/locale';
import SafeIcon from '../common/SafeIcon';
import { FiChevronLeft, FiChevronRight, FiPlus, FiClock, FiCalendar } from 'react-icons/fi';
import { EventModal } from '../components/Modals';

const CalendarView = () => {
  const { events, leads, addEvent, updateEvent } = useContext(CRMContext);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
  const endDate = endOfWeek(currentDate, { weekStartsOn: 1 });
  const daysInWeek = eachDayOfInterval({ start: startDate, end: endDate });

  const nextWeek = () => setCurrentDate(addDays(currentDate, 7));
  const prevWeek = () => setCurrentDate(subDays(currentDate, 7));
  const today = () => setCurrentDate(new Date());

  const openNewEvent = () => {
    setSelectedEvent(null);
    setIsModalOpen(true);
  };

  const openEditEvent = evt => {
    setSelectedEvent(evt);
    setIsModalOpen(true);
  };

  return (
    <div className="max-w-6xl mx-auto h-full flex flex-col">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold w-48 capitalize" style={{ color: '#0F172A' }}>
            {format(startDate, 'MMMM yyyy', { locale: es })}
          </h1>
          <div className="flex items-center bg-white rounded-lg overflow-hidden" style={{ border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <button onClick={prevWeek} className="p-2 transition-colors" style={{ color: '#64748B', borderRight: '1px solid #E2E8F0' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F8FAFC'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
              <SafeIcon icon={FiChevronLeft} />
            </button>
            <button onClick={today} className="px-4 py-2 text-sm font-semibold transition-colors" style={{ color: '#0F172A', borderRight: '1px solid #E2E8F0' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F8FAFC'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
              Hoy
            </button>
            <button onClick={nextWeek} className="p-2 transition-colors" style={{ color: '#64748B' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F8FAFC'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
              <SafeIcon icon={FiChevronRight} />
            </button>
          </div>
        </div>
        
        <button onClick={openNewEvent} className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold text-sm w-full sm:w-auto justify-center transition-all" style={{ backgroundColor: '#2563EB', boxShadow: '0 1px 3px rgba(37,99,235,0.3)' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1D4ED8'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2563EB'}>
          <SafeIcon icon={FiPlus} /> Agregar Evento
        </button>
      </div>

      <div className="flex-1 bg-white rounded-xl overflow-hidden flex flex-col" style={{ border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        {/* Day headers */}
        <div className="grid grid-cols-7" style={{ borderBottom: '1px solid #E2E8F0', backgroundColor: '#F8FAFC' }}>
          {daysInWeek.map(day => (
            <div key={day.toISOString()} className="p-3 text-center" style={{ borderRight: '1px solid #E2E8F0' }}>
              <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: '#94A3B8' }}>{format(day, 'EEE', { locale: es })}</p>
              <p className="text-lg mt-1 font-bold" style={{ color: isToday(day) ? '#2563EB' : '#0F172A' }}>{format(day, 'd')}</p>
              {isToday(day) && <div className="w-1.5 h-1.5 rounded-full mx-auto mt-1" style={{ backgroundColor: '#2563EB' }} />}
            </div>
          ))}
        </div>

        {/* Day columns */}
        <div className="flex-1 grid grid-cols-7 overflow-y-auto min-h-[400px]">
          {daysInWeek.map(day => {
            const dayEvents = events.filter(e => isSameDay(new Date(e.date), day)).sort((a, b) => new Date(a.date) - new Date(b.date));
            return (
              <div key={day.toISOString()} className="p-2 space-y-2 min-h-full" style={{ borderRight: '1px solid #F1F5F9' }}>
                {dayEvents.map(evt => {
                  const lead = leads.find(l => l.id === evt.leadId);
                  return (
                    <div key={evt.id} onClick={() => openEditEvent(evt)} className="p-2 rounded-lg cursor-pointer text-xs transition-all" style={evt.status === 'completed' ? { backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', opacity: 0.6 } : evt.type === 'task' ? { backgroundColor: '#FFFBEB', border: '1px solid #FDE68A' } : { backgroundColor: '#F0FDFA', border: '1px solid #99F6E4' }} onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'} onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                      <div className="flex items-center gap-1 font-bold mb-1" style={{ color: evt.type === 'task' ? '#D97706' : '#14B8A6' }}>
                        <SafeIcon icon={evt.type === 'task' ? FiClock : FiCalendar} /> {format(new Date(evt.date), 'HH:mm')}
                      </div>
                      <p className="font-semibold truncate" style={{ color: '#0F172A' }}>{evt.title}</p>
                      {lead && <p className="truncate mt-0.5" style={{ color: '#64748B' }}>{lead.name}</p>}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      <EventModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={data => selectedEvent ? updateEvent(data.id, data) : addEvent(data)} leads={leads} initialData={selectedEvent} />
    </div>
  );
};

export default CalendarView;